import { useState } from 'react';
import { Home, Users, TrendingUp, UserCog, Users2, Settings, Cloud, Code, Gavel, Sparkles, Phone, MessageSquareText, FolderKanban, FileText, Package, Inbox, Bot } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import PRIsMEManifesteModal from '../components/PRIsMEManifesteModal';
import AIAssistantSetupPanel from '../components/AIAssistantSetupPanel';
import AIAssistantModal from '../components/AIAssistantModal';
import { useAuth } from '../contexts/AuthContext';

type MenuItem = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
};

// Tous les items avec leur section (pour le filtre par utilisateur)
const ALL_ITEM_IDS = [
  'accueil',
  'direction', 'ventes', 'cybersecurite', 'infonuagique', 'programmation', 'ia-agentic', 'bztelecom', 'adminrh', 'gestionnaires', 'comites',
  'projets', 'projets-v2', 'projet-eh', 'gestiondocumentaire', 'catalogue-actifs', 'contenu-revision',
] as const;

// Section Accueil (seul)
const sectionAccueil = [
  { id: 'accueil' as const, label: 'Accueil', icon: Home, path: '/' },
];

// Section Département
const sectionDepartement: MenuItem[] = [
  { id: 'direction' as const, label: 'Service & Direction TI', icon: Users, path: '/direction-ti' },
  { id: 'ventes' as const, label: 'Ventes & Marketing', icon: TrendingUp, path: '/directeur-compte' },
  // NOTE: on conserve l'id historique 'cybersecurite' pour les règles d'accès existantes,
  // mais on pointe la route vers la nouvelle section "Solutions d’affaires".
  { id: 'cybersecurite' as const, label: 'Solutions d’affaires', icon: () => <img src='/icons/solutions-affaires.png' alt='Solutions d’affaires icon' className='w-3.5 h-3.5 shrink-0' />, path: '/solutions-affaires' },
  { id: 'infonuagique' as const, label: 'Infonuagique', icon: Cloud, path: '/infonuagique' },
  { id: 'programmation' as const, label: 'Programmation & IA', icon: Code, path: '/programmation-ia' },
  { id: 'ia-agentic' as const, label: 'IA agentic', icon: Bot, path: '/ia-agentic' },
  { id: 'bztelecom' as const, label: 'BZ Telecom', icon: Phone, path: '/bz-telecom' },
  { id: 'adminrh' as const, label: 'Administration & RH', icon: UserCog, path: '/admin-rh' },
  { id: 'gestionnaires' as const, label: 'Gestionnaires', icon: Users2, path: '/gestionnaires' },
  { id: 'comites' as const, label: 'Comité', icon: Gavel, path: '/comites' },
];

// Section Opérationnel
const sectionOperationnel = [
  // Masqué à la demande de JM (2026-10-23) — la page /projets reste accessible par URL directe
  // { id: 'projets' as const, label: 'Projets', icon: FolderKanban, path: '/projets' },
  { id: 'projets-v2' as const, label: 'Pilotage projets', icon: FolderKanban, path: '/projets-v2' },
  { id: 'projet-eh' as const, label: 'Projet PRIsME', icon: FolderKanban, path: '/projet-eh' },
  { id: 'gestiondocumentaire' as const, label: 'Gestion documentaire', icon: FileText, path: '/gestion-documentaire' },
  { id: 'catalogue-actifs' as const, label: 'Catalogue des actifs', icon: Package, path: '/catalogue-actifs' },
  { id: 'contenu-revision' as const, label: 'Contenu en révision', icon: Inbox, path: '/contenu-en-revision' },
];


// Emails autorisés à voir la section Projets (TactiCE intégré)
const PROJETS_ALLOWED_EMAILS = [
  'dany.bourassa@bzinc.ca',
  'eric.hains@bzinc.ca',
  'ehains@bzinc.ca',
  'philippe.bolduc@bzinc.ca',
  'jean-michel.caouette@bzinc.ca',
  'jean-pierre.leclerc@bzinc.ca',
  'francis.marquis@bzinc.ca',
  'pierre-luc.fortin@bzinc.ca',
  'vincent.bourassa@bzinc.ca',
  'stephane.paradis@bzinc.ca',
  'mikael.drouin-bouchard@bzinc.ca',
  'eric.durand@bzinc.ca',
];

// Emails avec restrictions (mêmes droits - Administration, RH, Infonuagique)
const RESTRICTED_USER_EMAILS = [
  'richard.delisle@bzinc.ca',
  'emilie.quintal@bzinc.ca',
  'melissa.pelletier@bzinc.ca',
  'louis-alexandre.hebert@bzinc.ca'
];

// Accès ultra-restreint: Ventes & Marketing uniquement
const VENTES_REUNION_ONLY_EMAILS = [
  'jessica.pratt@bzinc.ca',
  'rosalie.emond@bzinc.ca',
  'edita.puhovac@bzinc.ca',
  'tim.gaudet@bzinc.ca',
  'michael.roy@bzinc.ca',
  'zachary.seguin@bzinc.ca',
];

// Accès restreint: Cybersécurité + Gestion documentaire (Cybersécurité uniquement)
const MELISSA_MORENCY_EMAIL = 'melissa.morency@bzinc.ca';

const MELISSA_MORENCY_ALLOWED_SECTIONS = ['direction', 'ventes', 'cybersecurite', 'programmation', 'ia-agentic', 'gestiondocumentaire'] as const;

const CYBER_ONLY_EMAILS = [
  'mikael.drouin-bouchard@bzinc.ca',
  'philippe.bolduc@bzinc.ca',
];

// Email de Martin Carrier (BZ Telecom) avec accès limité
const MARTIN_CARRIER_EMAIL = 'martin.carrier@bzinc.ca';

// Email de Pierre-Luc Fortin (accès limité au menu)
const PIERRE_LUC_FORTIN_EMAIL = 'pierre-luc.fortin@bzinc.ca';

// Stéphane Paradis — tout sauf Comité CA, Dany CA/CE (inclut Administration & RH)
const STEPHANE_PARADIS_EMAIL = 'stephane.paradis@bzinc.ca';
const STEPHANE_PARADIS_ALLOWED_SECTIONS = ['accueil', 'direction', 'ventes', 'cybersecurite', 'infonuagique', 'programmation', 'ia-agentic', 'bztelecom', 'adminrh', 'gestionnaires', 'comites', 'projets', 'projets-v2', 'projet-eh', 'gestiondocumentaire', 'catalogue-actifs'] as const;

// Emails autorisés à voir « Administration vector AI » (setup LEX / knowledge base)
const ADMIN_VECTOR_AI_ALLOWED_EMAILS = [
  'eric.hains@bzinc.ca',
  'jean-michel.caouette@bzinc.ca',
  'dany.bourassa@bzinc.ca',
  'jean-pierre.leclerc@bzinc.ca',
  'pierre-luc.fortin@bzinc.ca',
  'eric.durand@bzinc.ca',
];

// Sections autorisées pour les utilisateurs avec restrictions (Admin, RH, Infonuagique)
const RESTRICTED_USER_ALLOWED_SECTIONS = ['accueil', 'infonuagique', 'adminrh', 'gestionnaires', 'comites'];

// Sections autorisées pour Martin Carrier (BZ Telecom + Programmation & IA)
const MARTIN_CARRIER_ALLOWED_SECTIONS = ['accueil', 'programmation', 'ia-agentic', 'bztelecom', 'comites'];

// Sections autorisées pour Pierre-Luc Fortin (inclut projets et catalogue actifs)
const PIERRE_LUC_FORTIN_ALLOWED_SECTIONS = ['accueil', 'direction', 'ventes', 'cybersecurite', 'infonuagique', 'comites', 'projets', 'projets-v2', 'projet-eh', 'catalogue-actifs'];
const VENTES_REUNION_ONLY_ALLOWED_SECTIONS = ['ventes'] as const;
// Inclut les entrées projet pour que les utilisateurs cyber-only explicitement listés dans PROJETS_ALLOWED_EMAILS
// voient les menus projet (sinon filterItems les exclut avant même canSeeProjets).
const CYBER_ONLY_ALLOWED_SECTIONS = ['cybersecurite', 'gestiondocumentaire', 'projets', 'projets-v2', 'projet-eh'] as const;

function filterItems<T extends { id: string }>(items: T[], allowedIds: readonly string[]): T[] {
  return items.filter((item) => allowedIds.includes(item.id));
}

export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const [isPRIsMEModalOpen, setIsPRIsMEModalOpen] = useState(false);
  const [isAdminSetupOpen, setIsAdminSetupOpen] = useState(false);
  const [isLEXModalOpen, setIsLEXModalOpen] = useState(false);

  const isPrismeAdmin =
    !!user?.email &&
    [
      'dany.bourassa@bzinc.ca',
      'jean-pierre.leclerc@bzinc.ca',
      'eric.hains@bzinc.ca',
      'francis.marquis@bzinc.ca',
      'jean-michel.caouette@bzinc.ca',
      'francois.delisle@bzinc.ca',
      'eric.durand@bzinc.ca',
    ].includes(user.email.toLowerCase());

  const isRestrictedUser = user?.email && RESTRICTED_USER_EMAILS.includes(user.email.toLowerCase());
  const isMelissaMorency = user?.email?.toLowerCase() === MELISSA_MORENCY_EMAIL;
  const isVentesReunionOnlyUser = user?.email && VENTES_REUNION_ONLY_EMAILS.includes(user.email.toLowerCase());
  const isCyberOnlyUser = user?.email && CYBER_ONLY_EMAILS.includes(user.email.toLowerCase());
  const isMartinCarrier = user?.email?.toLowerCase() === MARTIN_CARRIER_EMAIL.toLowerCase();
  const isPierreLucFortin = user?.email?.toLowerCase() === PIERRE_LUC_FORTIN_EMAIL.toLowerCase();
  const isStephaneParadis = user?.email?.toLowerCase() === STEPHANE_PARADIS_EMAIL.toLowerCase();

  let allowedIds: readonly string[] = ALL_ITEM_IDS;
  if (isMelissaMorency) {
    allowedIds = MELISSA_MORENCY_ALLOWED_SECTIONS;
  } else if (isVentesReunionOnlyUser) {
    allowedIds = VENTES_REUNION_ONLY_ALLOWED_SECTIONS;
  } else if (isCyberOnlyUser) {
    allowedIds = CYBER_ONLY_ALLOWED_SECTIONS;
  } else if (isMartinCarrier) {
    allowedIds = MARTIN_CARRIER_ALLOWED_SECTIONS;
  } else if (isPierreLucFortin) {
    allowedIds = PIERRE_LUC_FORTIN_ALLOWED_SECTIONS;
  } else if (isStephaneParadis) {
    allowedIds = STEPHANE_PARADIS_ALLOWED_SECTIONS;
  } else if (isRestrictedUser) {
    allowedIds = RESTRICTED_USER_ALLOWED_SECTIONS;
  }

  const canSeeProjets = user?.email && PROJETS_ALLOWED_EMAILS.includes(user.email.toLowerCase());
  const visibleAccueil = (isMelissaMorency || isVentesReunionOnlyUser || isCyberOnlyUser) ? [] : sectionAccueil;
  const visibleDepartement = filterItems(sectionDepartement, allowedIds);

  const operationnelItems = canSeeProjets
    ? filterItems(sectionOperationnel, allowedIds)
    : filterItems(sectionOperationnel, allowedIds).filter((item) => item.id !== 'projets-v2' && item.id !== 'projet-eh');

  const showParametreAdmin = !isRestrictedUser && !isMartinCarrier && !isPierreLucFortin && !isStephaneParadis;

  const canSeeAdminVectorAI =
    !!user?.email && ADMIN_VECTOR_AI_ALLOWED_EMAILS.includes(user.email.toLowerCase());

  const isActive = (path: string, id: string) => {
    if (id === 'projets') return location.pathname === '/projets';
    if (id === 'projets-v2') return location.pathname === '/projets-v2' || location.pathname.startsWith('/projets-v2/');
    if (id === 'projet-eh') return location.pathname.startsWith('/projet-eh');
    return location.pathname === path;
  };

  const renderLinkItem = (item: MenuItem) => {
    const Icon = item.icon;
    const active = isActive(item.path, item.id);
    return (
      <li key={item.id}>
        <Link
          to={item.path}
          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all duration-200 group ${
            active ? 'bg-bz-blue text-white' : 'hover:bg-white/10 text-gray-300 hover:text-white'
          }`}
        >
          <Icon className="w-3.5 h-3.5 shrink-0" />
          <span className="font-medium text-xs leading-tight">{item.label}</span>
        </Link>
      </li>
    );
  };

  return (
    <>
      <aside className="fixed left-0 top-0 h-screen w-52 bg-bz-dark text-white flex flex-col">
        <div className="p-2.5 border-b border-white/10">
          <button
            type="button"
            onClick={() => setIsPRIsMEModalOpen(true)}
            className="text-left w-full group transition-all duration-200 hover:opacity-90"
          >
            <img
              src="/PrismeLogo.png"
              alt="PRIsME Logo"
              className="h-10 w-auto group-hover:opacity-90 transition-opacity cursor-pointer"
            />
            <p className="text-[10px] text-gray-400 mt-1 group-hover:text-gray-300 transition-colors leading-tight">
              Plateforme interne BZ Inc.
            </p>
          </button>
        </div>

        <nav className="flex-1 p-2 overflow-y-auto min-h-0 space-y-2">
          {/* Accueil — seul */}
          <ul className="space-y-0.5">
            {visibleAccueil.map((item) => renderLinkItem(item))}
          </ul>

          {/* Section Département */}
          {visibleDepartement.length > 0 && (
            <>
              <p className="px-2.5 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-500">
                Département
              </p>
              <ul className="space-y-0.5">
                {visibleDepartement.map((item) => renderLinkItem(item))}
              </ul>
            </>
          )}

          {/* Section Opérationnel */}
          {operationnelItems.length > 0 && (
            <>
              <p className="px-2.5 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-500">
                Opérationnel
              </p>
              <ul className="space-y-0.5">
                {operationnelItems.map((item) => renderLinkItem(item))}
              </ul>
            </>
          )}

          {/* Section Paramètre (visible aussi pour Pierre-Luc et Stéphane pour accès Commentaires) */}
          {(showParametreAdmin || isPrismeAdmin || isPierreLucFortin || isStephaneParadis) && (
            <>
              <p className="px-2.5 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-500">
                Paramètre
              </p>
              <ul className="space-y-0.5">
                {showParametreAdmin && canSeeAdminVectorAI && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setIsAdminSetupOpen(true)}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200 group text-left"
                      title="Configuration AI Assistant (vector)"
                    >
                      <Settings className="w-3.5 h-3.5 shrink-0 group-hover:rotate-90 transition-transform duration-300" />
                      <span className="font-medium text-xs leading-tight">Administration vector AI</span>
                    </button>
                  </li>
                )}
                {(isPrismeAdmin || isStephaneParadis || isPierreLucFortin) && (
                  <li>
                    <Link
                      to="/gestion-parties-prenantes"
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all duration-200 group ${
                        location.pathname === '/gestion-parties-prenantes'
                          ? 'bg-bz-blue text-white'
                          : 'hover:bg-white/10 text-gray-300 hover:text-white'
                      }`}
                      title="Clients, parties prenantes & accès (Administration PRIsME BZ)"
                    >
                      <Users className="w-3.5 h-3.5 shrink-0" />
                      <span className="font-medium text-xs leading-tight">Gestion parties prenantes</span>
                    </Link>
                  </li>
                )}
                {(isPrismeAdmin || isPierreLucFortin || isStephaneParadis) && (
                  <li>
                    <Link
                      to="/direction-ti/commentaires"
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all duration-200 group ${
                        location.pathname.startsWith('/direction-ti/commentaires')
                          ? 'bg-bz-blue text-white'
                          : 'hover:bg-white/10 text-gray-300 hover:text-white'
                      }`}
                      title="Commentaires / Amélioration continue"
                    >
                      <MessageSquareText className="w-3.5 h-3.5 shrink-0" />
                      <span className="font-medium text-xs leading-tight">Commentaires / Amélioration continue</span>
                    </Link>
                  </li>
                )}
              </ul>
            </>
          )}
        </nav>

        {/* LEX Assistant PRIsME — en bas, toujours visible une fois connecté */}
        <div className="border-t border-white/10 shrink-0 flex flex-col">
          {user && (
            <Link
              to="/mon-agent"
              className="w-full flex items-center gap-2 px-2.5 py-2 text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200 group"
              title="LEX Assistant PRIsME - Mon agent"
            >
              <div className="relative">
                <Sparkles className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-400 rounded-full border border-bz-dark animate-pulse" />
              </div>
              <span className="text-[10px] font-semibold">LEX Assistant PRIsME</span>
            </Link>
          )}
          <div className="p-2">
            <p className="text-[9px] text-gray-400 text-center">© 2025 BZ Inc.</p>
            <p className="text-[8px] text-gray-500 text-center mt-0.5">v1.3.0</p>
          </div>
        </div>
      </aside>

      <PRIsMEManifesteModal
        isOpen={isPRIsMEModalOpen}
        onClose={() => setIsPRIsMEModalOpen(false)}
      />

      <AIAssistantModal
        isOpen={isLEXModalOpen}
        onClose={() => setIsLEXModalOpen(false)}
      />

      {isAdminSetupOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h2 className="text-xl font-bold text-gray-900">Configuration AI Assistant</h2>
              <button
                type="button"
                onClick={() => setIsAdminSetupOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="text-2xl text-gray-500">&times;</span>
              </button>
            </div>
            <div className="p-6">
              <AIAssistantSetupPanel />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
